import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, PLAN_LABELS } from "../lib/config";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LogOut, Users, DollarSign, Clock, Phone, MessageCircle, Mail, Loader2, Camera, Pencil, X, Send, Megaphone, CheckCircle2, XCircle, MessagesSquare, MessageSquare, KeyRound, Search, Trash2, Gift, Trophy, Download, FileText, DollarSign as DollarIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import { resizeImageToBase64 } from "../lib/image";
import { motion, AnimatePresence } from "framer-motion";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { AccountingOverview } from "../components/admin/AccountingOverview";
import { SquareResetPanel } from "../components/admin/SquareResetPanel";

const usPhoneDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};
const isValidUsPhone = (value) => {
  const digits = usPhoneDigits(value);
  return digits.length === 10 && !"01".includes(digits[0]) && !"01".includes(digits[3]);
};
const formatUsPhone = (value) => {
  const digits = usPhoneDigits(value).slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [sponsorship, setSponsorship] = useState(null);
  const reloadSponsorship = async () => {
    try {
      const sp = await api.get("/admin/sponsorship/summary");
      setSponsorship(sp.data);
    } catch { /* silent */ }
  };
  const [sortMode, setSortMode] = useState("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // all | today | yesterday | 7d | custom
  const [phoneFilter, setPhoneFilter] = useState("all"); // all | needs-fix
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStatus, setReportStatus] = useState("all"); // all | full | partial | credit | sponsored
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      nav("/admin/login");
      return;
    }
    const load = async () => {
      try {
        const me = await api.get("/admin/me").catch(() => null);
        const role = me?.data?.role;
        if (role && role !== "admin") {
          nav("/admin/logistica", { replace: true });
          return;
        }
        const [s, r, u, sp] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/registrations"),
          api.get("/admin/conversations/unread-count").catch(() => ({ data: { unread: 0 } })),
          api.get("/admin/sponsorship/summary").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setRegs(r.data);
        setUnread(u.data.unread || 0);
        setSponsorship(sp.data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("admin_token");
          nav("/admin/login");
        } else {
          toast.error("Error cargando datos");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(() => {
      api.get("/admin/conversations/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const downloadReport = async (format) => {
    if (downloading) return;
    setDownloading(format);
    try {
      const params = new URLSearchParams();
      if (reportStatus && reportStatus !== "all") params.set("status", reportStatus);
      if (reportFrom) params.set("date_from", reportFrom);
      if (reportTo) params.set("date_to", reportTo);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const path = format === "pdf" ? "/admin/registrations/export.pdf" : "/admin/registrations/export";
      const res = await api.get(`${path}${qs}`, { responseType: "blob" });
      const mime = format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "").slice(0, 12);
      const suffix = reportStatus !== "all" ? `-${reportStatus}` : "";
      link.download = `operacion-rescate-inscritos${suffix}-${stamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const label = {
        full: "pagaron completo", partial: "abonaron", credit: "crédito sin abono",
        sponsored: "becados", all: "todos",
      }[reportStatus] || "todos";
      toast.success(`${format.toUpperCase()} descargado (${label})`);
      setReportOpen(false);
    } catch (e) {
      toast.error(`Error descargando ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white px-6 lg:px-12 py-5 flex items-center justify-between sticky top-0 z-50 border-b border-white/10">
        <div>
          <div className="font-heading uppercase text-xl tracking-wider">Operación Rescate</div>
          <div className="text-yellow-400 text-[10px] uppercase tracking-[0.3em]">Panel Admin</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => nav("/admin/logistica")}
            data-testid="logistics-tab-btn"
            className="text-white hover:text-yellow-400 hover:bg-white/5"
          >
            <MapPin className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Logística</span>
          </Button>
          <Button
            variant="ghost"
            onClick={() => setReportOpen(true)}
            data-testid="open-report-modal-btn"
            className="text-white hover:text-yellow-400 hover:bg-white/5"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Reporte</span>
          </Button>
          <Button
            variant="ghost"
            onClick={() => nav("/admin/mensajes")}
            data-testid="messages-tab-btn"
            className="text-white hover:text-yellow-400 hover:bg-white/5 relative"
          >
            <MessageSquare className="w-4 h-4 mr-2" /> Mensajes
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full px-2 py-0.5 font-bold min-w-[20px] text-center" data-testid="messages-unread-badge">
                {unread}
              </span>
            )}
          </Button>
          <Button variant="ghost" onClick={logout} className="text-white hover:text-yellow-400 hover:bg-white/5" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 lg:p-10">
        <AccountingOverview
          stats={stats}
          regs={regs}
          onOpenReports={() => setReportOpen(true)}
          onOpenCleanup={() => nav("/admin/logistica?tab=cleanup")}
        />

        {/* Sponsorship / Recaudación de Jóvenes */}
        {sponsorship && (sponsorship.total_people > 0 || sponsorship.total_budget > 0) && (
          <div data-testid="sponsorship-panel" className="mb-8 bg-gradient-to-br from-pink-50 to-fuchsia-50 border-2 border-pink-200 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-pink-700 font-bold mb-1">
                  ★ Fondo de Becas · Privado
                </div>
                <h2 className="font-heading text-2xl text-pink-900 mb-1">
                  Recaudación aplicada: <span className="text-3xl">${sponsorship.total_sponsored_amount.toLocaleString()}</span>
                </h2>
                <p className="text-sm text-pink-700">
                  {sponsorship.total_people} campista{sponsorship.total_people !== 1 ? "s" : ""} patrocinado{sponsorship.total_people !== 1 ? "s" : ""} · No visible para kiosco ni campistas
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {sponsorship.by_source.map((s) => (
                  <div key={s.source} className="bg-white rounded-lg px-3 py-2 border border-pink-200 min-w-[140px]">
                    <div className="text-[10px] uppercase tracking-widest text-pink-600 font-bold truncate" title={s.source}>{s.source}</div>
                    <div className="font-heading text-lg text-pink-900">${s.total.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500">{s.count} persona{s.count !== 1 ? "s" : ""}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Budget bar */}
            {(sponsorship.total_budget > 0 || sponsorship.total_people > 0) && (
              <div className="mt-4 bg-white rounded-lg p-3 border border-pink-200">
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="text-pink-800 uppercase tracking-widest">Presupuesto de becas</span>
                  <button
                    onClick={async () => {
                      const cur = sponsorship.total_budget || 0;
                      const v = window.prompt(`Presupuesto total de becas (actual: $${cur}):`, String(cur));
                      if (v === null) return;
                      const num = parseFloat(v);
                      if (isNaN(num) || num < 0) { toast.error("Valor inválido"); return; }
                      try {
                        await api.patch("/admin/beca-fund", { total_budget: num });
                        toast.success(`Presupuesto: $${num}`);
                        reloadSponsorship();
                      } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                    }}
                    data-testid="edit-beca-budget-btn"
                    className="text-orange-600 hover:text-orange-800 underline"
                  >
                    Editar
                  </button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Total: <b>${(sponsorship.total_budget || 0).toLocaleString()}</b></span>
                  <span className="text-gray-700">Usado: <b className="text-pink-700">${sponsorship.total_sponsored_amount.toLocaleString()}</b></span>
                  <span className="text-gray-700">Disponible: <b className={sponsorship.available_budget < 0 ? "text-red-600" : "text-green-700"}>${(sponsorship.available_budget || 0).toLocaleString()}</b></span>
                </div>
                {sponsorship.total_budget > 0 && (
                  <div className="mt-2 w-full h-2 bg-pink-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${sponsorship.available_budget < 0 ? "bg-red-500" : "bg-pink-500"}`}
                      style={{ width: `${Math.min(100, (sponsorship.total_sponsored_amount / sponsorship.total_budget) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            {sponsorship.people.length > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-bold text-pink-800 hover:text-pink-900 select-none" data-testid="sponsorship-list-toggle">
                  Ver lista completa ({sponsorship.people.length})
                </summary>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {sponsorship.people.map((p) => (
                    <div key={p.id} className="bg-white/70 rounded-lg px-3 py-2 border border-pink-100 text-sm flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 truncate">{p.full_name}</div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {p.camper_number_display} · {p.sponsor_source} · {p.sponsor_note || "—"}
                        </div>
                      </div>
                      <div className="font-heading text-lg text-pink-700 flex-shrink-0">${p.sponsor_amount}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Registrations table */}
        <Card>
          <CardHeader className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="font-heading uppercase text-2xl">Inscritos</CardTitle>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider font-bold">Ordenar:</label>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  data-testid="sort-select"
                  className="h-9 px-2 rounded border border-gray-300 text-sm font-semibold"
                >
                  <option value="newest">📅 Más recientes</option>
                  <option value="oldest">📅 Más antiguos</option>
                  <option value="progress_desc">💰 Más pagado</option>
                  <option value="progress_asc">💰 Menos pagado</option>
                  <option value="name">🔤 Nombre A-Z</option>
                </select>
              </div>
            </div>
            {/* Search bar */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
                placeholder="Buscar por nombre, email, teléfono o plan..."
                className="w-full h-10 pl-9 pr-9 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  data-testid="search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Date filter */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-bold">Fecha:</label>
              {[
                { id: "all", label: "Todos" },
                { id: "today", label: "Hoy" },
                { id: "yesterday", label: "Ayer" },
                { id: "7d", label: "Últimos 7 días" },
                { id: "custom", label: "Personalizado" },
              ].map((o) => (
                <button
                  key={o.id}
                  onClick={() => setDateFilter(o.id)}
                  data-testid={`date-filter-${o.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${dateFilter === o.id ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  {o.label}
                </button>
              ))}
              {dateFilter === "custom" && (
                <div className="flex items-center gap-2 ml-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="date-custom-from"
                    className="h-8 px-2 rounded border border-gray-300 text-xs"
                  />
                  <span className="text-xs text-gray-500">a</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="date-custom-to"
                    className="h-8 px-2 rounded border border-gray-300 text-xs"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-bold">Teléfonos:</label>
              <button
                onClick={() => setPhoneFilter("all")}
                data-testid="phone-filter-all"
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${phoneFilter === "all" ? "bg-black text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                Todos
              </button>
              <button
                onClick={() => setPhoneFilter("needs-fix")}
                data-testid="phone-filter-needs-fix"
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${phoneFilter === "needs-fix" ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
              >
                <Phone className="w-3 h-3" /> Por corregir ({regs.filter((reg) => !isValidUsPhone(reg.phone)).length})
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {regs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">Aún no hay inscritos.</div>
            ) : (
              (() => {
                const sortFn = (a, b) => {
                  switch (sortMode) {
                    case "oldest": return (a.created_at || "").localeCompare(b.created_at || "");
                    case "progress_desc": return (b.percentage_paid || 0) - (a.percentage_paid || 0);
                    case "progress_asc": return (a.percentage_paid || 0) - (b.percentage_paid || 0);
                    case "name": return (a.full_name || "").localeCompare(b.full_name || "");
                    case "newest":
                    default: return (b.created_at || "").localeCompare(a.created_at || "");
                  }
                };
                // Filter by search query (case-insensitive, normalized accents)
                const norm = (s) => (s || "")
                  .toString()
                  .normalize("NFD").replace(/\p{Diacritic}/gu, "")
                  .toLowerCase().trim();
                const q = norm(searchQuery);
                const qDigits = (searchQuery || "").replace(/\D/g, "");
                // Date filter
                const dateInRange = (r) => {
                  if (dateFilter === "all") return true;
                  if (!r.created_at) return false;
                  const d = new Date(r.created_at);
                  const now = new Date();
                  if (dateFilter === "today") {
                    return d.toDateString() === now.toDateString();
                  }
                  if (dateFilter === "yesterday") {
                    const y = new Date(now); y.setDate(y.getDate() - 1);
                    return d.toDateString() === y.toDateString();
                  }
                  if (dateFilter === "7d") {
                    const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 7);
                    return d >= cutoff;
                  }
                  if (dateFilter === "custom") {
                    const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
                    const to = customTo ? new Date(customTo + "T23:59:59") : null;
                    if (from && d < from) return false;
                    if (to && d > to) return false;
                    return true;
                  }
                  return true;
                };
                const dateFilteredRegs = regs.filter(dateInRange);
                const phoneFilteredRegs = phoneFilter === "needs-fix"
                  ? dateFilteredRegs.filter((reg) => !isValidUsPhone(reg.phone))
                  : dateFilteredRegs;
                const filteredRegs = !q ? phoneFilteredRegs : phoneFilteredRegs.filter((r) => {
                  const phoneDigits = (r.phone || "").replace(/\D/g, "");
                  return (
                    norm(r.full_name).includes(q) ||
                    norm(r.email).includes(q) ||
                    norm(r.plan_type).includes(q) ||
                    norm(r.age_category).includes(q) ||
                    norm(r.phone).includes(q) ||
                    (qDigits && phoneDigits.includes(qDigits))
                  );
                });
                const sortedRegs = filteredRegs.sort(sortFn);
                const filtered = filteredRegs.length !== regs.length;
                const updateRegistration = (updated) => setRegs((current) => current.map((reg) => reg.id === updated.id ? { ...reg, ...updated } : reg));
                return (
                  <>
                    {filtered && (
                      <div className="text-xs text-gray-500 mb-3" data-testid="search-result-count">
                        Mostrando {filteredRegs.length} de {regs.length}
                        {dateFilter !== "all" && <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px] font-bold uppercase">📅 {dateFilter === "today" ? "Hoy" : dateFilter === "yesterday" ? "Ayer" : dateFilter === "7d" ? "Últimos 7 días" : "Rango custom"}</span>}
                      </div>
                    )}
                    <Tabs defaultValue="all">
                      <TabsList>
                        <TabsTrigger value="all">Todos ({sortedRegs.length})</TabsTrigger>
                        <TabsTrigger value="adult">Adultos ({sortedRegs.filter(r => r.age_category === "adult").length})</TabsTrigger>
                        <TabsTrigger value="child">Niños ({sortedRegs.filter(r => r.age_category === "child").length})</TabsTrigger>
                      </TabsList>
                      <TabsContent value="all"><RegTable regs={sortedRegs} showDate onSponsorshipChanged={reloadSponsorship} onRegistrationChanged={updateRegistration} /></TabsContent>
                      <TabsContent value="adult"><RegTable regs={sortedRegs.filter(r => r.age_category === "adult")} showDate onSponsorshipChanged={reloadSponsorship} onRegistrationChanged={updateRegistration} /></TabsContent>
                      <TabsContent value="child"><RegTable regs={sortedRegs.filter(r => r.age_category === "child")} showDate onSponsorshipChanged={reloadSponsorship} onRegistrationChanged={updateRegistration} /></TabsContent>
                    </Tabs>
                  </>
                );
              })()
            )}
          </CardContent>
        </Card>

        {/* Square unmatched payments */}
        <div className="mt-10">
          <UnmatchedPayments regs={regs} />
        </div>

        {/* Overpayments + Completed campers (auto-recurring control) */}
        <div className="mt-10">
          <SquareRecurringControl />
        </div>

        <SquareResetPanel />

        {/* Broadcast panel */}
        <div className="mt-10">
          <BroadcastPanel regs={regs} />
        </div>

        {/* Reminder config */}
        <div className="mt-10">
          <ReminderConfigPanel />
        </div>

        {/* Auto-reply rules */}
        <div className="mt-10">
          <AutoReplyPanel />
        </div>

        {/* Promotions panel */}
        <div className="mt-10">
          <PromotionsPanel />
        </div>
      </main>

      {/* Report download modal with filters */}
      <AnimatePresence>
        {reportOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4"
            onClick={() => !downloading && setReportOpen(false)}
            data-testid="report-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl"
              data-testid="report-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1 text-black">Descargar reporte</h3>
              <p className="text-sm text-gray-600 mb-5">Elige la categoría contable que quieres revisar</p>

              {/* Status filter */}
              <div className="mb-5">
                <label className="text-xs uppercase tracking-widest text-gray-700 font-bold mb-2 block">
                  Estado de pago
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: "all", label: "Todos", icon: Users },
                    { id: "full", label: "Pagaron completo", icon: CheckCircle2 },
                    { id: "partial", label: "Abonaron", icon: DollarIcon },
                    { id: "credit", label: "Crédito sin abono", icon: Clock },
                    { id: "sponsored", label: "Becados", icon: Gift },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = reportStatus === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setReportStatus(opt.id)}
                        data-testid={`report-status-${opt.id}`}
                        className={`p-3 rounded-xl border-2 text-left transition-colors ${
                          active
                            ? "border-yellow-500 bg-yellow-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1 ${active ? "text-yellow-600" : "text-gray-500"}`} />
                        <div className={`font-heading uppercase text-xs tracking-wider ${active ? "text-yellow-700" : "text-gray-700"}`}>
                          {opt.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date range */}
              <div className="mb-5">
                <label className="text-xs uppercase tracking-widest text-gray-700 font-bold mb-2 block">
                  Rango de fechas de inscripción <span className="text-gray-400 normal-case font-normal">(opcional)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</div>
                    <input
                      type="date"
                      value={reportFrom}
                      onChange={(e) => setReportFrom(e.target.value)}
                      data-testid="report-date-from"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Hasta</div>
                    <input
                      type="date"
                      value={reportTo}
                      onChange={(e) => setReportTo(e.target.value)}
                      data-testid="report-date-to"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                </div>
                {(reportFrom || reportTo) && (
                  <button
                    onClick={() => { setReportFrom(""); setReportTo(""); }}
                    data-testid="report-clear-dates"
                    className="mt-2 text-xs text-yellow-700 hover:text-yellow-800 font-semibold underline"
                  >
                    Limpiar fechas
                  </button>
                )}
              </div>

              {/* Format buttons */}
              <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setReportOpen(false)}
                  disabled={!!downloading}
                  data-testid="report-cancel"
                  className="sm:flex-none"
                >
                  Cancelar
                </Button>
                <div className="flex-1" />
                <Button
                  onClick={() => downloadReport("csv")}
                  disabled={!!downloading}
                  data-testid="report-download-csv"
                  className="bg-black hover:bg-gray-800 text-white font-bold uppercase tracking-widest text-xs"
                >
                  {downloading === "csv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Descargar CSV
                </Button>
                <Button
                  onClick={() => downloadReport("pdf")}
                  disabled={!!downloading}
                  data-testid="report-download-pdf"
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold uppercase tracking-widest text-xs"
                >
                  {downloading === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Descargar PDF
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RegTable({ regs, showDate, onSponsorshipChanged, onRegistrationChanged }) {
  const [editingPhoto, setEditingPhoto] = useState(null); // reg object
  const [editingPayments, setEditingPayments] = useState(null); // reg object
  const [photoLoading, setPhotoLoading] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null); // reg object
  const [newPlan, setNewPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [editingTotal, setEditingTotal] = useState(null); // reg object
  const [newTotal, setNewTotal] = useState("");
  const [savingTotal, setSavingTotal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null); // reg object
  const [sponsorSource, setSponsorSource] = useState("Recaudación Jóvenes");
  const [sponsorAmount, setSponsorAmount] = useState("");
  const [sponsorNote, setSponsorNote] = useState("");
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [editingName, setEditingName] = useState(null); // reg object
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [editingPaid, setEditingPaid] = useState(null); // reg object
  const [newPaid, setNewPaid] = useState("");
  const [savingPaid, setSavingPaid] = useState(false);
  const [paymentReg, setPaymentReg] = useState(null); // reg being paid manually
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payNote, setPayNote] = useState("");
  const [payReceipt, setPayReceipt] = useState(null); // base64 data URL
  const payReceiptInputRef = useRef(null);
  const [savingPay, setSavingPay] = useState(false);
  const [localRegs, setLocalRegs] = useState(regs);
  useEffect(() => { setLocalRegs(regs); }, [regs]);

  const onPickPhoto = async (e, reg) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Imagen muy grande (máx 12MB)"); return; }
    setPhotoLoading(true);
    try {
      const dataUrl = await resizeImageToBase64(file, 400, 0.85);
      const { data } = await api.patch(`/admin/registrations/${reg.id}/photo`, { photo: dataUrl });
      setLocalRegs((prev) => prev.map((r) => (r.id === reg.id ? { ...r, photo: data.photo } : r)));
      setEditingPhoto(null);
      toast.success("Foto actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || err?.message || "Error al subir foto", { duration: 8000 });
    } finally {
      setPhotoLoading(false);
      if (e?.target) e.target.value = "";
    }
  };
  const removePhoto = async (reg) => {
    setPhotoLoading(true);
    try {
      await api.patch(`/admin/registrations/${reg.id}/photo`, { photo: null });
      setLocalRegs((prev) => prev.map((r) => (r.id === reg.id ? { ...r, photo: null } : r)));
      setEditingPhoto(null);
      toast.success("Foto removida");
    } catch (err) {
      toast.error("No se pudo remover");
    } finally {
      setPhotoLoading(false);
    }
  };
  const savePlan = async () => {
    if (!editingPlan || !newPlan || newPlan === editingPlan.plan_type) {
      setEditingPlan(null);
      return;
    }
    setSavingPlan(true);
    try {
      const { data } = await api.patch(`/admin/registrations/${editingPlan.id}/plan`, { plan_type: newPlan });
      setLocalRegs((prev) => prev.map((r) => (r.id === editingPlan.id ? { ...r, ...data } : r)));
      toast.success(`Plan cambiado a ${PLAN_LABELS[newPlan]}`);
      setEditingPlan(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar el plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const saveTotal = async () => {
    if (!editingTotal) return;
    const amt = parseFloat(newTotal);
    if (!amt || amt <= 0) { toast.error("Ingresa un total válido"); return; }
    if (amt < (editingTotal.paid_amount || 0)) {
      toast.error(`No puede ser menor a lo ya pagado ($${editingTotal.paid_amount})`);
      return;
    }
    setSavingTotal(true);
    try {
      const { data } = await api.patch(`/admin/registrations/${editingTotal.id}/total`, { total_amount: amt });
      setLocalRegs((prev) => prev.map((r) => (r.id === editingTotal.id ? { ...r, ...data } : r)));
      toast.success(`Precio actualizado a $${amt}`);
      setEditingTotal(null);
      setNewTotal("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar el precio");
    } finally {
      setSavingTotal(false);
    }
  };

  const saveName = async () => {
    if (!editingName) return;
    const clean = newName.trim();
    if (clean.length < 2) { toast.error("El nombre es muy corto"); return; }
    setSavingName(true);
    try {
      const { data } = await api.patch(`/admin/registrations/${editingName.id}/name`, { full_name: clean });
      setLocalRegs((prev) => prev.map((r) => (r.id === editingName.id ? { ...r, ...data } : r)));
      toast.success("Nombre actualizado");
      setEditingName(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSavingName(false); }
  };

  const startPhoneEdit = (reg) => {
    setEditingPhone(reg);
    setNewPhone(usPhoneDigits(reg.phone).slice(0, 10));
  };

  const savePhone = async () => {
    if (!editingPhone) return;
    const digits = usPhoneDigits(newPhone);
    if (!isValidUsPhone(digits)) {
      toast.error("Ingresa un número válido de EE. UU. de 10 dígitos");
      return;
    }
    setSavingPhone(true);
    try {
      const { data } = await api.patch(`/admin/registrations/${editingPhone.id}/phone`, { phone: digits });
      setLocalRegs((prev) => prev.map((reg) => reg.id === editingPhone.id ? { ...reg, ...data } : reg));
      onRegistrationChanged?.(data);
      toast.success(`Teléfono actualizado: ${data.phone}`);
      setEditingPhone(null);
      setNewPhone("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar el teléfono");
    } finally {
      setSavingPhone(false);
    }
  };

  const togglePaymentMode = async (reg) => {
    const target = reg.payment_mode === "paid" ? "credit" : "paid";
    const label = target === "paid" ? "CONTADO (marcar todo pagado)" : "CRÉDITO (permitir cuotas)";
    if (!window.confirm(`Cambiar ${reg.full_name} a ${label}?`)) return;
    try {
      const { data } = await api.patch(`/admin/registrations/${reg.id}/payment-mode`, { payment_mode: target });
      setLocalRegs((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...data } : r)));
      toast.success(`Cambiado a ${target === "paid" ? "Contado" : "Crédito"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const cycleCategory = async (reg) => {
    const order = ["small", "child", "adult"];
    const label = { small: "3-6 años ($55)", child: "7-11 años ($150)", adult: "12+ años ($250)" };
    const cur = order.indexOf(reg.age_category);
    const next = order[(cur + 1) % 3];
    if (!window.confirm(`Cambiar ${reg.full_name} a ${label[next]}?\n\n¿Actualizar también el monto total al precio por defecto?`)) return;
    const resetTotal = window.confirm("¿Actualizar también el TOTAL al precio por defecto del nuevo grupo?\n\n(Cancelar = mantener el monto actual)");
    try {
      const { data } = await api.patch(`/admin/registrations/${reg.id}/age-category`, {
        age_category: next, reset_total: resetTotal,
      });
      setLocalRegs((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...data } : r)));
      toast.success(`Ahora es ${label[next]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const savePaid = async () => {
    if (!editingPaid) return;
    const amt = parseFloat(newPaid);
    if (isNaN(amt) || amt < 0) { toast.error("Monto inválido"); return; }
    if (amt > editingPaid.total_amount) {
      toast.error(`No puede ser mayor al total ($${editingPaid.total_amount})`);
      return;
    }
    setSavingPaid(true);
    try {
      const { data } = await api.patch(`/admin/registrations/${editingPaid.id}/paid-amount`, { paid_amount: amt });
      setLocalRegs((prev) => prev.map((r) => (r.id === editingPaid.id ? { ...r, ...data } : r)));
      toast.success(`Pagado ajustado a $${amt}`);
      setEditingPaid(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSavingPaid(false); }
  };


  const saveSponsorship = async (reg, isSponsored, source, amount, note) => {
    try {
      const { data } = await api.patch(`/admin/registrations/${reg.id}/sponsorship`, {
        is_sponsored: isSponsored,
        sponsor_source: source || "",
        sponsor_amount: Number(amount) || 0,
        sponsor_note: note || "",
        mark_paid: isSponsored,
      });
      setLocalRegs((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...data } : r)));
      // notify parent to reload sponsorship summary panel
      if (onSponsorshipChanged) onSponsorshipChanged();
      toast.success(isSponsored ? "Patrocinio guardado" : "Patrocinio removido");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
      return false;
    }
  };

  const savePay = async () => {
    if (!paymentReg) return;
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Ingresa un monto válido"); return; }
    setSavingPay(true);
    try {
      const { data } = await api.post(`/admin/registrations/${paymentReg.id}/manual-payment`, {
        registration_id: paymentReg.id,
        amount: amt,
        method: payMethod,
        note: payNote || null,
        receipt_photo: payReceipt || null,
      });
      setLocalRegs((prev) => prev.map((r) => (r.id === paymentReg.id ? { ...r, ...data.registration } : r)));
      toast.success(`Pago de $${amt} registrado · ${data.installments_marked.length} cuota(s) marcada(s)`);
      setPaymentReg(null);
      setPayAmount("");
      setPayNote("");
      setPayReceipt(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pago");
    } finally {
      setSavingPay(false);
    }
  };

  const [sendingCredsId, setSendingCredsId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const sendCredentials = async (r) => {
    if (!window.confirm(`¿Enviar credenciales por SMS a ${r.full_name} (${r.phone})?\n\nCosto: ~$0.008 USD`)) return;
    setSendingCredsId(r.id);
    try {
      await api.post(`/admin/registrations/${r.id}/send-credentials`);
      toast.success(`Credenciales enviadas a ${r.full_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar SMS");
    } finally {
      setSendingCredsId(null);
    }
  };

  const deleteRegistration = async (r) => {
    const ok1 = window.confirm(`¿Eliminar a ${r.full_name}?\n\nEsto borrará SU inscripción, pagos registrados y vínculos con conversaciones SMS.\n\nEsta acción NO se puede deshacer.`);
    if (!ok1) return;
    const typed = window.prompt(`Para confirmar, escribe el nombre EXACTO: "${r.full_name}"`);
    if (!typed || typed.trim().toLowerCase() !== (r.full_name || "").trim().toLowerCase()) {
      toast.error("Nombre no coincide. Eliminación cancelada.");
      return;
    }
    setDeletingId(r.id);
    try {
      const { data } = await api.delete(`/admin/registrations/${r.id}`);
      setLocalRegs((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(`${data.name} eliminado · ${data.payments_removed} pago(s) removido(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const buildWaMessage = (r) => {    const remaining = r.remaining_amount;
    const next = r.installment_amount;
    return encodeURIComponent(
      `Hola ${r.full_name}! 👋\n\nTe escribimos desde Operación Rescate. Tu próxima cuota de $${next} (${PLAN_LABELS[r.plan_type]?.toLowerCase()}) ya está disponible.\n\nTotal pagado: $${r.paid_amount}\nRestante: $${remaining}\n\n¡Gracias por ser parte! 🔥`
    );
  };
  const cleanPhone = (p) => usPhoneDigits(p);
  return (
    <div className="overflow-x-auto" data-testid="registrations-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Inscrito</TableHead>
            <TableHead>Categoría</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Pagado</TableHead>
            <TableHead>Pendiente</TableHead>
            {showDate && <TableHead>Fecha inscripción</TableHead>}
            <TableHead>Contacto</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {localRegs.map((r) => (
            <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setEditingPhoto(r)}
                    data-testid={`admin-photo-btn-${r.id}`}
                    className="relative w-11 h-11 rounded-full border-2 border-yellow-400 bg-gradient-to-br from-yellow-100 to-orange-100 overflow-hidden flex items-center justify-center group flex-shrink-0"
                    title="Editar foto"
                  >
                    {r.photo ? (
                      <img src={r.photo} alt="foto" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-4 h-4 text-orange-500" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <Pencil className="w-3 h-3 text-white opacity-0 group-hover:opacity-100" />
                    </div>
                  </button>
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      <span>{r.full_name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingName(r);
                          setNewName(r.full_name || "");
                        }}
                        data-testid={`edit-name-btn-${r.id}`}
                        title="Editar nombre"
                        className="text-gray-400 hover:text-orange-500 p-0.5"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); togglePaymentMode(r); }}
                        data-testid={`toggle-mode-btn-${r.id}`}
                        title="Cambiar entre Contado y Crédito"
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded border hover:opacity-80 ${
                          r.payment_mode === "paid"
                            ? "bg-green-100 text-green-800 border-green-300"
                            : "bg-orange-100 text-orange-800 border-orange-300"
                        }`}
                      >
                        {r.payment_mode === "paid" ? "CONTADO" : "CRÉDITO"}
                      </button>
                      {r.camper_number_display && (
                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300" data-testid={`admin-camper-num-${r.id}`} title="Número de campista">
                          {r.camper_number_display}
                        </span>
                      )}
                      {r.is_sponsored && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSponsor(r);
                            setSponsorSource(r.sponsor_source || "Recaudación Jóvenes");
                            setSponsorAmount(String(r.sponsor_amount || r.total_amount || ""));
                            setSponsorNote(r.sponsor_note || "");
                          }}
                          data-testid={`sponsor-badge-${r.id}`}
                          title={`Patrocinado por ${r.sponsor_source || "—"} ($${r.sponsor_amount || 0})`}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-pink-100 text-pink-800 border border-pink-300 hover:bg-pink-200"
                        >
                          ★ BECA
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{r.email}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => { setEditingTotal(r); setNewTotal(String(r.total_amount || "")); }}
                  data-testid={`admin-total-btn-${r.id}`}
                  className="text-left hover:bg-yellow-50 rounded px-2 py-1 -mx-2 group transition-colors"
                  title="Editar precio total"
                >
                  <Badge
                    variant={r.age_category === "adult" ? "default" : "secondary"}
                    onClick={(e) => { e.stopPropagation(); cycleCategory(r); }}
                    data-testid={`edit-category-btn-${r.id}`}
                    className="cursor-pointer hover:ring-2 hover:ring-orange-400"
                    title="Click para cambiar entre Adulto / Niño / 3-6"
                  >
                    {r.age_category === "adult" ? "Adulto" : r.age_category === "child" ? "Niño" : "3-6"}
                  </Badge>
                  <div className="text-xs mt-1 flex items-center gap-1 text-gray-600 font-semibold">
                    ${r.total_amount}
                    <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-orange-500" />
                  </div>
                </button>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => { setEditingPlan(r); setNewPlan(r.plan_type); }}
                  data-testid={`admin-plan-btn-${r.id}`}
                  className="text-left hover:bg-gray-50 rounded px-2 py-1 -mx-2 group"
                  title="Cambiar plan"
                >
                  <div className="text-sm font-semibold flex items-center gap-1">
                    {PLAN_LABELS[r.plan_type]}
                    <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-orange-500" />
                  </div>
                  <div className="text-xs text-gray-500">${r.installment_amount} x {r.num_installments}</div>
                </button>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingPayments(r)}
                    data-testid={`admin-payments-btn-${r.id}`}
                    className="text-left hover:bg-green-50 rounded px-2 py-1 -mx-2 group transition-colors"
                    title="Editar pagos cuota por cuota"
                  >
                    <div className="text-green-600 font-bold flex items-center gap-1">
                      ${r.paid_amount}
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-orange-500" />
                    </div>
                    <div className="text-xs text-gray-500">{r.installments_paid} cuotas</div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPaid(r);
                      setNewPaid(String(r.paid_amount || 0));
                    }}
                    data-testid={`edit-paid-amount-btn-${r.id}`}
                    title="Ajustar monto pagado directamente"
                    className="p-1 rounded hover:bg-orange-100 text-orange-600 border border-orange-300"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                  </button>
                </div>
              </TableCell>
              <TableCell>
                <div className={`font-bold ${r.remaining_amount > 0 ? "text-orange-600" : "text-green-600"}`}>
                  ${r.remaining_amount}
                </div>
              </TableCell>
              {showDate && (
                <TableCell data-testid={`date-cell-${r.id}`}>
                  <div className="text-sm font-semibold">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "2-digit" }) : "—"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {r.created_at ? new Date(r.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: true }) : ""}
                  </div>
                </TableCell>
              )}
              <TableCell data-testid={`phone-cell-${r.id}`}>
                {editingPhone?.id === r.id ? (
                  <div className="flex items-center gap-1 min-w-[205px]" data-testid={`phone-editor-${r.id}`}>
                    <input
                      autoFocus
                      type="tel"
                      inputMode="numeric"
                      value={formatUsPhone(newPhone)}
                      onChange={(event) => setNewPhone(event.target.value.replace(/\D/g, "").slice(0, 10))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") savePhone();
                        if (event.key === "Escape") setEditingPhone(null);
                      }}
                      placeholder="614-555-1234"
                      data-testid={`phone-input-${r.id}`}
                      className={`w-32 h-9 px-2 rounded border text-sm font-mono focus:outline-none focus:ring-2 ${isValidUsPhone(newPhone) ? "border-green-400 focus:ring-green-300" : "border-red-300 focus:ring-red-200"}`}
                    />
                    <button onClick={savePhone} disabled={savingPhone || !isValidUsPhone(newPhone)} data-testid={`phone-save-${r.id}`} title="Guardar teléfono" className="w-8 h-8 grid place-items-center rounded bg-green-600 text-white disabled:opacity-40">
                      {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setEditingPhone(null)} disabled={savingPhone} data-testid={`phone-cancel-${r.id}`} title="Cancelar" className="w-8 h-8 grid place-items-center rounded bg-gray-200 text-gray-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => startPhoneEdit(r)}
                    data-testid={`phone-edit-${r.id}`}
                    title="Editar teléfono"
                    className={`group text-sm flex items-center gap-1.5 rounded px-2 py-1 -mx-2 transition-colors ${isValidUsPhone(r.phone) ? "text-blue-700 hover:bg-blue-50" : "text-red-700 bg-red-50 hover:bg-red-100 font-semibold"}`}
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{isValidUsPhone(r.phone) ? formatUsPhone(r.phone) : "Agregar / corregir"}</span>
                    <Pencil className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                  </button>
                )}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setPaymentReg(r); setPayAmount(String(r.installment_amount || "")); setPayMethod("cash"); setPayNote(""); }}
                    data-testid={`pay-btn-${r.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-black transition-colors"
                    title="Registrar pago (cash/transferencia)"
                  >
                    <DollarSign className="w-4 h-4" />
                  </button>
                  <a
                    href={isValidUsPhone(r.phone) ? `/admin/mensajes?phone=${encodeURIComponent("+1" + cleanPhone(r.phone))}` : undefined}
                    data-testid={`sms-btn-${r.id}`}
                    aria-disabled={!isValidUsPhone(r.phone)}
                    onClick={(event) => { if (!isValidUsPhone(r.phone)) event.preventDefault(); }}
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-white transition-colors ${isValidUsPhone(r.phone) ? "bg-blue-500 hover:bg-blue-600" : "bg-gray-300 cursor-not-allowed"}`}
                    title={isValidUsPhone(r.phone) ? "Chat SMS" : "Corrige el teléfono primero"}
                  >
                    <MessagesSquare className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => sendCredentials(r)}
                    disabled={sendingCredsId === r.id || !isValidUsPhone(r.phone)}
                    data-testid={`send-creds-btn-${r.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-purple-500 hover:bg-purple-600 text-white transition-colors disabled:opacity-50"
                    title="Enviar credenciales por SMS"
                  >
                    {sendingCredsId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  </button>
                  <a
                    href={isValidUsPhone(r.phone) ? `https://wa.me/1${cleanPhone(r.phone)}?text=${buildWaMessage(r)}` : undefined}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`whatsapp-btn-${r.id}`}
                    aria-disabled={!isValidUsPhone(r.phone)}
                    onClick={(event) => { if (!isValidUsPhone(r.phone)) event.preventDefault(); }}
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-white transition-colors ${isValidUsPhone(r.phone) ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 cursor-not-allowed"}`}
                    title={isValidUsPhone(r.phone) ? "Enviar recordatorio WhatsApp" : "Corrige el teléfono primero"}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </a>
                  <a
                    href={`mailto:${r.email}?subject=Operacion Rescate - Recordatorio cuota&body=Hola ${r.full_name}!`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                    title="Enviar email"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => deleteRegistration(r)}
                    disabled={deletingId === r.id}
                    data-testid={`delete-btn-${r.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
                    title="Eliminar inscripción"
                  >
                    {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Photo modal */}
      <AnimatePresence>
        {editingPhoto && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !photoLoading && setEditingPhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              data-testid="admin-photo-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-4">Foto de {editingPhoto.full_name}</h3>
              <div className="flex flex-col items-center gap-4">
                <div className="w-32 h-32 rounded-full border-4 border-yellow-400 bg-gradient-to-br from-yellow-100 to-orange-100 overflow-hidden flex items-center justify-center">
                  {photoLoading ? (
                    <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                  ) : editingPhoto.photo ? (
                    <img src={editingPhoto.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-10 h-10 text-orange-500" />
                  )}
                </div>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md cursor-pointer text-sm font-semibold">
                  <Camera className="w-4 h-4" />
                  {editingPhoto.photo ? "Cambiar foto" : "Subir foto"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onPickPhoto(e, editingPhoto)}
                    className="hidden"
                    data-testid="admin-photo-input"
                    disabled={photoLoading}
                  />
                </label>
                {editingPhoto.photo && (
                  <button
                    onClick={() => removePhoto(editingPhoto)}
                    disabled={photoLoading}
                    className="text-red-600 hover:text-red-700 text-sm font-semibold inline-flex items-center gap-1"
                    data-testid="admin-photo-remove"
                  >
                    <X className="w-3 h-3" /> Quitar foto
                  </button>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <Button variant="outline" onClick={() => setEditingPhoto(null)} disabled={photoLoading} data-testid="admin-photo-close">Cerrar</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Payments edit modal */}
      <AnimatePresence>
        {editingPayments && (
          <EditPaymentsModal
            reg={editingPayments}
            onClose={() => setEditingPayments(null)}
            onUpdated={(updated) => {
              setLocalRegs((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
              setEditingPayments(updated);
            }}
          />
        )}
      </AnimatePresence>


      {/* Plan modal */}
      <AnimatePresence>
        {editingPlan && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingPlan && setEditingPlan(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl"
              data-testid="admin-plan-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-2">Cambiar plan de {editingPlan.full_name}</h3>
              <p className="text-sm text-gray-600 mb-4">
                Cuotas pagadas se mantienen. Monto restante <strong>${editingPlan.remaining_amount}</strong> se reorganiza.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { id: "daily", label: "Diario" },
                  { id: "weekly", label: "Semanal" },
                  { id: "biweekly", label: "Quincenal" },
                  { id: "monthly", label: "Mensual" },
                ].map((p) => {
                  const active = newPlan === p.id;
                  const current = editingPlan.plan_type === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setNewPlan(p.id)}
                      data-testid={`admin-plan-option-${p.id}`}
                      className={`p-3 rounded-xl border-2 text-left ${active ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                    >
                      <div className="font-heading uppercase text-base text-black flex items-center justify-between">
                        {p.label}
                        {current && <span className="text-[9px] uppercase tracking-widest bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-bold">Actual</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingPlan(null)} disabled={savingPlan}>Cancelar</Button>
                <Button onClick={savePlan} disabled={savingPlan || !newPlan || newPlan === editingPlan.plan_type} data-testid="admin-plan-save" className="bg-orange-500 hover:bg-orange-600 text-white">
                  {savingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Total price edit modal */}
      <AnimatePresence>
        {editingTotal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingTotal && setEditingTotal(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="admin-total-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1">Editar precio total</h3>
              <p className="text-sm text-gray-600 mb-4">
                <strong>{editingTotal.full_name}</strong> · Actual: <strong>${editingTotal.total_amount}</strong> · Pagado: <strong className="text-green-600">${editingTotal.paid_amount}</strong>
              </p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[55, 150, 250].map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setNewTotal(String(preset))}
                    data-testid={`admin-total-preset-${preset}`}
                    className={`p-3 rounded-xl border-2 text-center transition-colors ${
                      parseFloat(newTotal) === preset ? "border-yellow-500 bg-yellow-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-heading text-lg text-black">${preset}</div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                      {preset === 55 ? "3-6 años" : preset === 150 ? "7-11 años" : "12+ años"}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mb-4">
                <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold mb-1 block">
                  O ingresa un monto personalizado
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                  <input
                    type="number"
                    value={newTotal}
                    onChange={(e) => setNewTotal(e.target.value)}
                    placeholder="Ej: 150"
                    step="0.01"
                    min={editingTotal.paid_amount || 0}
                    data-testid="admin-total-input"
                    className="w-full pl-7 pr-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-yellow-500 focus:outline-none"
                    autoFocus
                  />
                </div>
                {parseFloat(newTotal) > 0 && parseFloat(newTotal) < (editingTotal.paid_amount || 0) && (
                  <p className="text-xs text-red-500 mt-1">No puede ser menor a lo ya pagado (${editingTotal.paid_amount})</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                💡 Las cuotas pagadas se mantienen. El plan se recalcula automáticamente con el nuevo total.
              </p>

              {/* Patrocinio quick access */}
              <div className={`mb-4 rounded-lg p-3 ${editingTotal.is_sponsored ? "bg-pink-50 border border-pink-200" : "bg-gray-50 border border-gray-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    <div className="font-bold text-pink-800 text-xs uppercase tracking-widest mb-0.5">
                      {editingTotal.is_sponsored ? "★ Patrocinado" : "Beca / Patrocinio"}
                    </div>
                    {editingTotal.is_sponsored ? (
                      <div className="text-gray-700 text-xs">
                        <b>{editingTotal.sponsor_source || "—"}</b> · ${editingTotal.sponsor_amount || 0}
                        {editingTotal.sponsor_note ? ` · ${editingTotal.sponsor_note}` : ""}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-xs">Sin patrocinio (marca si alguien más pagará por este campista)</div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setEditingSponsor(editingTotal);
                      setSponsorSource(editingTotal.sponsor_source || "Recaudación Jóvenes");
                      setSponsorAmount(String(editingTotal.sponsor_amount || editingTotal.total_amount || ""));
                      setSponsorNote(editingTotal.sponsor_note || "");
                      setEditingTotal(null);
                    }}
                    data-testid="open-sponsor-btn"
                    className={`text-xs px-3 py-1.5 rounded font-bold uppercase tracking-widest ${editingTotal.is_sponsored ? "bg-pink-600 hover:bg-pink-700 text-white" : "bg-black hover:bg-gray-800 text-white"}`}
                  >
                    {editingTotal.is_sponsored ? "Editar" : "Marcar"}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingTotal(null)} disabled={savingTotal} data-testid="admin-total-cancel">Cancelar</Button>
                <Button
                  onClick={saveTotal}
                  disabled={savingTotal || !newTotal || parseFloat(newTotal) <= 0 || parseFloat(newTotal) < (editingTotal.paid_amount || 0)}
                  data-testid="admin-total-save"
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                >
                  {savingTotal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar precio"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sponsorship modal */}
      <AnimatePresence>
        {editingPaid && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingPaid && setEditingPaid(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="paid-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1">Ajustar monto pagado</h3>
              <p className="text-sm text-gray-600 mb-4">
                <strong>{editingPaid.full_name}</strong> · Total: <strong>${editingPaid.total_amount}</strong>
              </p>
              <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold mb-1 block">
                Nuevo monto pagado
              </label>
              <div className="relative mb-3">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                <input
                  type="number"
                  value={newPaid}
                  onChange={(e) => setNewPaid(e.target.value)}
                  step="0.01"
                  min="0"
                  max={editingPaid.total_amount}
                  autoFocus
                  data-testid="new-paid-input"
                  className="w-full pl-7 pr-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {[0, editingPaid.total_amount / 2, editingPaid.total_amount].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setNewPaid(String(preset))}
                    className="text-xs px-3 py-1 rounded border-2 border-gray-200 hover:border-orange-400 text-gray-700 font-bold"
                  >
                    ${preset}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mb-4">
                💡 Ejemplo: si alguien solo viene sábado y pagó $120 de $250, escribe 120. Las cuotas se ajustan automáticamente.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingPaid(null)} disabled={savingPaid} data-testid="paid-cancel">Cancelar</Button>
                <Button
                  onClick={savePaid}
                  disabled={savingPaid}
                  data-testid="paid-save"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                >
                  {savingPaid ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingName && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingName && setEditingName(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="name-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1">Editar nombre</h3>
              <p className="text-sm text-gray-600 mb-4">
                Actual: <strong>{editingName.full_name}</strong>
              </p>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="new-name-input"
                autoFocus
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-orange-500 focus:outline-none mb-4"
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingName(null)} disabled={savingName} data-testid="name-cancel">Cancelar</Button>
                <Button
                  onClick={saveName}
                  disabled={savingName}
                  data-testid="name-save"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                >
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingSponsor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingSponsor && setEditingSponsor(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="sponsor-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1 flex items-center gap-2">
                <span className="text-pink-600">★</span> Beca / Patrocinio
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                <strong>{editingSponsor.full_name}</strong> · Total: <strong>${editingSponsor.total_amount}</strong> · Pagado: <strong className="text-green-600">${editingSponsor.paid_amount}</strong>
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold mb-1 block">
                    Patrocinador
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {["Recaudación Jóvenes", "Iglesia", "Anónimo"].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setSponsorSource(preset)}
                        data-testid={`sponsor-preset-${preset}`}
                        className={`text-xs px-2 py-2 rounded-lg border-2 font-bold transition ${
                          sponsorSource === preset
                            ? "border-pink-500 bg-pink-50 text-pink-800"
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={sponsorSource}
                    onChange={(e) => setSponsorSource(e.target.value)}
                    placeholder="O escribe otro nombre..."
                    data-testid="sponsor-source-input"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-pink-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold mb-1 block">
                    Monto cubierto por el patrocinador
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {[55, 150, 250].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setSponsorAmount(String(preset))}
                        data-testid={`sponsor-amt-preset-${preset}`}
                        className={`text-xs px-2 py-2 rounded-lg border-2 font-bold transition ${
                          parseFloat(sponsorAmount) === preset
                            ? "border-pink-500 bg-pink-50 text-pink-800"
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                        }`}
                      >
                        ${preset}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                    <input
                      type="number"
                      value={sponsorAmount}
                      onChange={(e) => setSponsorAmount(e.target.value)}
                      placeholder="Ej: 250 (cubre todo)"
                      step="0.01"
                      min="0"
                      data-testid="sponsor-amount-input"
                      className="w-full pl-7 pr-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-pink-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold mb-1 block">
                    Nota interna (opcional)
                  </label>
                  <input
                    type="text"
                    value={sponsorNote}
                    onChange={(e) => setSponsorNote(e.target.value)}
                    placeholder="Ej: Cubre inscripción + transporte"
                    data-testid="sponsor-note-input"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-black focus:border-pink-500 focus:outline-none"
                  />
                </div>

                <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 text-xs text-pink-900">
                  Al guardar, todas las cuotas pendientes se marcarán como <b>pagadas</b> (fuente: sponsorship).
                  Este dato es privado — no lo verá el campista ni el kiosco.
                </div>
              </div>

              <div className="flex justify-between gap-2 mt-5">
                {editingSponsor.is_sponsored && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setSavingSponsor(true);
                      const ok = await saveSponsorship(editingSponsor, false);
                      setSavingSponsor(false);
                      if (ok) setEditingSponsor(null);
                    }}
                    disabled={savingSponsor}
                    data-testid="sponsor-remove-btn"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Remover beca
                  </Button>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" onClick={() => setEditingSponsor(null)} disabled={savingSponsor}>Cancelar</Button>
                  <Button
                    onClick={async () => {
                      if (!sponsorSource.trim()) { toast.error("Escribe el nombre del patrocinador"); return; }
                      if (!(parseFloat(sponsorAmount) > 0)) { toast.error("Ingresa un monto válido"); return; }
                      setSavingSponsor(true);
                      const ok = await saveSponsorship(editingSponsor, true, sponsorSource.trim(), parseFloat(sponsorAmount), sponsorNote.trim());
                      setSavingSponsor(false);
                      if (ok) setEditingSponsor(null);
                    }}
                    disabled={savingSponsor}
                    data-testid="sponsor-save-btn"
                    className="bg-pink-600 hover:bg-pink-700 text-white font-bold"
                  >
                    {savingSponsor ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSponsor.is_sponsored ? "Actualizar beca" : "Marcar patrocinado")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual payment modal */}
      <AnimatePresence>
        {paymentReg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !savingPay && setPaymentReg(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="manual-payment-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-1">Registrar pago</h3>
              <div className="text-sm text-gray-600 mb-5">
                {paymentReg.full_name} · Restante <strong className="text-orange-600">${paymentReg.remaining_amount}</strong>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="pay-amount">Monto (USD)</Label>
                  <input
                    id="pay-amount"
                    data-testid="pay-amount-input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-lg font-semibold"
                    autoFocus
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[paymentReg.installment_amount, paymentReg.installment_amount * 2, paymentReg.remaining_amount].filter((v, i, a) => v > 0 && a.indexOf(v) === i).map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPayAmount(String(v))}
                        className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200"
                        data-testid={`pay-quick-${i}`}
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Método</Label>
                  <RadioGroup value={payMethod} onValueChange={setPayMethod} className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { id: "cash", label: "💵 Efectivo" },
                      { id: "transfer", label: "🏦 Transferencia" },
                      { id: "square_manual", label: "💳 Square (manual)" },
                      { id: "other", label: "🔹 Otro" },
                    ].map((o) => (
                      <label key={o.id} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm ${payMethod === o.id ? "border-orange-400 bg-orange-50" : "border-gray-200"}`} data-testid={`pay-method-${o.id}`}>
                        <RadioGroupItem value={o.id} id={`m-${o.id}`} />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor="pay-note">Nota (opcional)</Label>
                  <Textarea
                    id="pay-note"
                    data-testid="pay-note-input"
                    value={payNote}
                    onChange={(e) => setPayNote(e.target.value)}
                    rows={2}
                    placeholder="Ej: recibido en el campamento del domingo"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Comprobante de pago (opcional)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <input
                      id="pay-receipt"
                      ref={payReceiptInputRef}
                      data-testid="pay-receipt-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 3 * 1024 * 1024) { toast.error("Máximo 3 MB"); return; }
                        const r = new FileReader();
                        r.onload = () => setPayReceipt(r.result);
                        r.readAsDataURL(file);
                      }}
                    />
                    <label
                      htmlFor="pay-receipt"
                      data-testid="pay-receipt-label"
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border-2 border-dashed border-gray-300 hover:border-yellow-500 hover:bg-yellow-50 cursor-pointer text-sm text-gray-600"
                    >
                      <Camera className="w-4 h-4" />
                      {payReceipt ? "Cambiar foto" : "Adjuntar foto (Zelle, transferencia…)"}
                    </label>
                    {payReceipt && (
                      <div className="relative flex-shrink-0">
                        <img src={payReceipt} alt="Comprobante" className="w-14 h-14 rounded-md object-cover border border-gray-300" />
                        <button
                          onClick={() => {
                            setPayReceipt(null);
                            if (payReceiptInputRef.current) payReceiptInputRef.current.value = "";
                          }}
                          data-testid="pay-receipt-clear"
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => { setPaymentReg(null); setPayReceipt(null); }} disabled={savingPay}>Cancelar</Button>
                <Button onClick={savePay} disabled={savingPay} data-testid="pay-save-btn" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold">
                  {savingPay ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" /> Registrar</>}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


function BroadcastPanel({ regs }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("reminder_payment");
  const [customBody, setCustomBody] = useState("");
  const [channel, setChannel] = useState("sms"); // sms | whatsapp
  const [filterType, setFilterType] = useState("unpaid"); // all | unpaid | fully_paid | selected
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/message-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const currentTemplate = templates.find((t) => t.id === templateId);
  const bodyForSend = templateId === "custom" ? customBody : currentTemplate?.body || "";

  // Auto-fill custom body when switching to "Personalizado"
  useEffect(() => {
    if (templateId !== "custom" && currentTemplate?.body) {
      setCustomBody(currentTemplate.body);
    }
  }, [templateId, currentTemplate]);

  const filterCount = (() => {
    if (filterType === "all") return regs.length;
    if (filterType === "unpaid") return regs.filter((r) => r.remaining_amount > 0).length;
    if (filterType === "fully_paid") return regs.filter((r) => r.percentage_paid >= 100).length;
    if (filterType === "selected") return selectedIds.length;
    return 0;
  })();

  const doPreview = async () => {
    if (!bodyForSend?.trim()) {
      toast.error("Escribe un mensaje");
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/broadcast/preview", {
        channel,
        template_id: templateId === "custom" ? "custom" : templateId,
        custom_body: templateId === "custom" ? customBody : undefined,
        filter_type: filterType,
        registration_ids: filterType === "selected" ? selectedIds : undefined,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en preview");
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async () => {
    if (!preview) {
      toast.error("Primero revisa la vista previa");
      return;
    }
    if (channel === "sms" && !window.confirm(`¿Confirmas enviar SMS a ${preview.count} personas? Costo aprox $${(preview.count * 0.008).toFixed(2)} USD.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", {
        channel,
        template_id: templateId === "custom" ? "custom" : templateId,
        custom_body: templateId === "custom" ? customBody : undefined,
        filter_type: filterType,
        registration_ids: filterType === "selected" ? selectedIds : undefined,
      });
      setResult(data);
      if (channel === "sms") {
        toast.success(`SMS enviados: ${data.sent}/${data.total}${data.failed ? ` · ${data.failed} fallaron` : ""}`);
      } else {
        toast.success(`${data.total} mensajes listos para enviar por WhatsApp`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Card data-testid="broadcast-panel">
      <CardHeader>
        <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-orange-500" /> Enviar Mensajes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Channel selector */}
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 block">Canal</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setChannel("sms")}
              data-testid="broadcast-channel-sms"
              className={`p-4 rounded-xl border-2 text-left transition-all ${channel === "sms" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <MessagesSquare className="w-5 h-5 text-blue-500" />
                <span className="font-semibold">SMS automático</span>
              </div>
              <div className="text-xs text-gray-600">Twilio · ~$0.008 USD por mensaje · envío instantáneo</div>
            </button>
            <button
              type="button"
              onClick={() => setChannel("whatsapp")}
              data-testid="broadcast-channel-whatsapp"
              className={`p-4 rounded-xl border-2 text-left transition-all ${channel === "whatsapp" ? "border-green-500 bg-green-50 ring-2 ring-green-200" : "border-gray-200 hover:border-gray-300"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold">WhatsApp asistido</span>
              </div>
              <div className="text-xs text-gray-600">Gratis · clic por persona · usas tu WhatsApp</div>
            </button>
          </div>
        </div>

        {/* Template */}
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 block">Plantilla</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTemplateId(t.id)}
                data-testid={`broadcast-template-${t.id}`}
                className={`p-3 rounded-lg border text-left text-sm transition-all ${templateId === t.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
              >
                <div className="font-semibold">{t.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Message editor */}
        <div>
          <Label htmlFor="broadcast-body" className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 block">
            Mensaje {templateId !== "custom" && <span className="text-gray-400 normal-case font-normal">(editable)</span>}
          </Label>
          <Textarea
            id="broadcast-body"
            data-testid="broadcast-body"
            value={customBody}
            onChange={(e) => { setCustomBody(e.target.value); if (templateId !== "custom") setTemplateId("custom"); }}
            rows={5}
            placeholder="Escribe tu mensaje..."
            className="font-mono text-sm"
          />
          <div className="text-[11px] text-gray-500 mt-2 leading-relaxed">
            Variables: <code className="bg-gray-100 px-1">{`{nombre}`}</code> <code className="bg-gray-100 px-1">{`{cuota}`}</code> <code className="bg-gray-100 px-1">{`{pagado}`}</code> <code className="bg-gray-100 px-1">{`{total}`}</code> <code className="bg-gray-100 px-1">{`{restante}`}</code> <code className="bg-gray-100 px-1">{`{pct}`}</code> <code className="bg-gray-100 px-1">{`{plan}`}</code> <code className="bg-gray-100 px-1">{`{link_pago}`}</code> <code className="bg-gray-100 px-1">{`{link_cuenta}`}</code>
          </div>
        </div>

        {/* Audience filter */}
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 block">Destinatarios</Label>
          <RadioGroup value={filterType} onValueChange={setFilterType} className="grid sm:grid-cols-2 gap-2">
            {[
              { id: "all", label: `Todos los inscritos (${regs.length})` },
              { id: "unpaid", label: `Con cuotas pendientes (${regs.filter((r) => r.remaining_amount > 0).length})` },
              { id: "fully_paid", label: `Ya pagaron todo (${regs.filter((r) => r.percentage_paid >= 100).length})` },
              { id: "selected", label: `Selección manual (${selectedIds.length})` },
            ].map((o) => (
              <label key={o.id} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${filterType === o.id ? "border-orange-400 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`} data-testid={`broadcast-filter-${o.id}`}>
                <RadioGroupItem value={o.id} id={`filter-${o.id}`} />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </RadioGroup>

          {filterType === "selected" && (
            <div className="mt-3 max-h-48 overflow-y-auto border rounded-lg divide-y" data-testid="broadcast-select-list">
              {regs.map((r) => (
                <label key={r.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleSelected(r.id)} className="rounded" />
                  <span className="font-medium flex-1 truncate">{r.full_name}</span>
                  <span className="text-xs text-gray-500">{r.phone}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button onClick={doPreview} disabled={previewing || !bodyForSend?.trim() || filterCount === 0} variant="outline" data-testid="broadcast-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Vista previa ({filterCount})
          </Button>
          {preview && (
            <Button onClick={doSend} disabled={sending} className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold uppercase tracking-wider" data-testid="broadcast-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {channel === "sms" ? `Enviar ${preview.count} SMS` : `Preparar ${preview.count} WhatsApp`}
            </Button>
          )}
        </div>

        {/* Preview list */}
        {preview && !result && (
          <div className="border rounded-xl p-4 bg-gray-50" data-testid="broadcast-preview-list">
            <div className="font-semibold mb-3">Vista previa · {preview.count} destinatario{preview.count !== 1 ? "s" : ""}</div>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {preview.recipients.slice(0, 10).map((r) => (
                <div key={r.id} className="bg-white p-3 rounded-lg border text-sm">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-semibold">{r.full_name}</span>
                    <span className="text-xs text-gray-500">{r.phone_e164 || `❌ ${r.phone}`}</span>
                  </div>
                  <div className="text-gray-700 whitespace-pre-wrap text-xs">{r.message}</div>
                </div>
              ))}
              {preview.count > 10 && <div className="text-xs text-gray-500 text-center">… y {preview.count - 10} más</div>}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="border-2 border-green-300 rounded-xl p-4 bg-green-50" data-testid="broadcast-result">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="font-bold text-green-800">
                {channel === "sms"
                  ? `Resultado: ${result.sent} enviados · ${result.failed} fallaron`
                  : `${result.total} mensajes listos para WhatsApp`}
              </span>
            </div>
            {channel === "whatsapp" ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {result.results.map((r) => (
                  <div key={r.registration_id} className="bg-white p-3 rounded-lg border flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm">{r.full_name}</div>
                      <div className="text-xs text-gray-500">{r.phone_e164 || r.phone}</div>
                    </div>
                    {r.wa_url ? (
                      <a href={r.wa_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-semibold" data-testid={`wa-send-${r.registration_id}`}>
                        <MessageCircle className="w-3 h-3" /> Enviar
                      </a>
                    ) : (
                      <span className="text-xs text-red-600">❌ Sin teléfono</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto text-sm">
                {result.results.map((r) => (
                  <div key={r.registration_id} className="flex items-center gap-2 bg-white p-2 rounded border">
                    {r.status === "sent" ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                    <span className="font-medium flex-1 truncate">{r.full_name}</span>
                    <span className="text-xs text-gray-500">{r.phone_e164 || r.phone}</span>
                    {r.error && <span className="text-xs text-red-600">{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => { setResult(null); setPreview(null); }} className="mt-3" data-testid="broadcast-clear">
              Cerrar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function UnmatchedPayments({ regs }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null); // unmatched item
  const [selectedReg, setSelectedReg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/square/unmatched");
      setItems(data.unmatched);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doAssign = async () => {
    if (!assigning || !selectedReg) return;
    setBusy(true);
    try {
      await api.post(`/admin/square/unmatched/${assigning.id}/assign`, { registration_id: selectedReg });
      toast.success(`Pago de $${assigning.amount} asignado correctamente`);
      setAssigning(null);
      setSelectedReg("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al asignar");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (item) => {
    if (!window.confirm(`¿Descartar el pago de $${item.amount}?`)) return;
    try {
      await api.delete(`/admin/square/unmatched/${item.id}`);
      toast.success("Pago descartado");
      load();
    } catch (e) {
      toast.error("Error al descartar");
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <Card className="border-orange-300 bg-orange-50/30" data-testid="unmatched-card">
      <CardHeader>
        <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-orange-500" /> Pagos sin asignar ({items.length})
        </CardTitle>
        <div className="text-xs text-gray-600">Pagos de Square sin coincidencia automática. Las sugerencias verdes muestran el match más probable.</div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="bg-white p-3 rounded-lg border" data-testid={`unmatched-row-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-green-600 text-lg">${it.amount}</div>
                  <div className="text-xs text-gray-600 truncate">
                    {it.email || "Sin email"} · {it.phone || "Sin teléfono"}
                    {it.buyer_name ? ` · 👤 ${it.buyer_name}` : ""}
                  </div>
                  {it.note && <div className="text-xs italic text-orange-700 mt-1">📝 Nota: &ldquo;{it.note}&rdquo;</div>}
                  <div className="text-[10px] text-gray-400 mt-1">{new Date(it.created_at).toLocaleString()} · ID: {it.square_payment_id?.slice(-8)}</div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button size="sm" onClick={() => setAssigning(it)} data-testid={`assign-btn-${it.id}`} className="bg-orange-500 hover:bg-orange-600 text-white">
                    Asignar manual
                  </Button>
                  <button onClick={() => doDelete(it)} className="text-red-500 hover:text-red-700 text-xs underline" data-testid={`unmatched-delete-${it.id}`}>
                    Descartar
                  </button>
                </div>
              </div>
              {(it.suggested_candidates || []).length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">💡 Sugerencias automáticas:</div>
                  <div className="space-y-1.5">
                    {it.suggested_candidates.map((c) => (
                      <div key={c.registration_id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                        <div className={`text-xs font-bold rounded-full px-2 py-0.5 ${c.score >= 0.85 ? "bg-green-500 text-white" : c.score >= 0.65 ? "bg-yellow-500 text-white" : "bg-gray-400 text-white"}`}>
                          {Math.round(c.score * 100)}%
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{c.full_name}</div>
                          <div className="text-[10px] text-gray-500 truncate">debe ${c.remaining_amount} · match: &ldquo;{c.matched_on}&rdquo;</div>
                        </div>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/square/unmatched/${it.id}/assign`, { registration_id: c.registration_id });
                              toast.success(`Asignado a ${c.full_name}`);
                              load();
                            } catch (e) { toast.error("Error"); }
                          }}
                          data-testid={`quick-assign-${it.id}-${c.registration_id}`}
                          className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-3"
                        >
                          Asignar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amount-based suggestions */}
              {(it.amount_matches?.exact_count > 0 || it.amount_matches?.near_count > 0) && (
                <div className="mt-3 border-t pt-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700 mb-2">
                    💰 Inscritos cuya cuota es ${it.amount}
                    {it.amount_matches.exact_count > 0 && (
                      <span className="ml-2 text-blue-600">({it.amount_matches.exact_count} exactos{it.amount_matches.near_count > 0 ? `, ${it.amount_matches.near_count} cercanos` : ""})</span>
                    )}
                  </div>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {it.amount_matches.exact.map((c) => (
                      <div key={c.registration_id} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300">
                          {c.camper_number_display || "—"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{c.full_name}</div>
                          <div className="text-[10px] text-gray-500 truncate">{c.plan_type} · cuota ${c.installment_amount} · {c.pending_installments} pendientes · debe ${c.remaining_amount}</div>
                        </div>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/square/unmatched/${it.id}/assign`, { registration_id: c.registration_id });
                              toast.success(`Asignado a ${c.full_name}`);
                              load();
                            } catch (e) { toast.error("Error"); }
                          }}
                          data-testid={`amount-assign-${it.id}-${c.registration_id}`}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-7 px-3"
                        >
                          Asignar
                        </Button>
                      </div>
                    ))}
                    {it.amount_matches.near.length > 0 && (
                      <details className="text-[10px] text-gray-500">
                        <summary className="cursor-pointer mt-1 hover:text-gray-700">Ver {it.amount_matches.near_count} cercanos (±$1)</summary>
                        <div className="space-y-1.5 mt-1">
                          {it.amount_matches.near.map((c) => (
                            <div key={c.registration_id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                              <div className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300">
                                {c.camper_number_display || "—"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{c.full_name}</div>
                                <div className="text-[10px] text-gray-500 truncate">cuota ${c.installment_amount} · {c.pending_installments} pendientes</div>
                              </div>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/square/unmatched/${it.id}/assign`, { registration_id: c.registration_id });
                                    toast.success(`Asignado a ${c.full_name}`);
                                    load();
                                  } catch (e) { toast.error("Error"); }
                                }}
                                className="bg-gray-500 hover:bg-gray-600 text-white text-xs h-7 px-3"
                              >
                                Asignar
                              </Button>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>

      <AnimatePresence>
        {assigning && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !busy && setAssigning(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              data-testid="assign-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-2">Asignar pago de ${assigning.amount}</h3>
              <p className="text-sm text-gray-600 mb-4">Email del pagador: <strong>{assigning.email || "(sin email)"}</strong></p>
              <Label>Inscripción</Label>
              <select
                value={selectedReg}
                onChange={(e) => setSelectedReg(e.target.value)}
                className="w-full mt-1 h-11 px-3 rounded border border-gray-300"
                data-testid="assign-select"
              >
                <option value="">— Selecciona un inscrito —</option>
                {regs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name} · {r.email} · debe ${r.remaining_amount}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setAssigning(null)} disabled={busy}>Cancelar</Button>
                <Button onClick={doAssign} disabled={busy || !selectedReg} data-testid="assign-confirm" className="bg-orange-500 hover:bg-orange-600 text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Asignar pago"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}


function ReminderConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reminder/config");
      setCfg(data);
    } catch (e) {/* */}
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const merged = { ...cfg, ...patch };
      const { data } = await api.patch("/admin/reminder/config", merged);
      setCfg(data);
      if (patch.enabled !== undefined) {
        toast.success(patch.enabled ? "Recordatorio automático activado" : "Desactivado");
      } else {
        toast.success("Configuración guardada");
      }
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm("¿Ejecutar el recordatorio AHORA a todos con cuotas pendientes? Si está configurado como SMS, se cobrará.")) return;
    setRunningNow(true);
    try {
      const { data } = await api.post("/admin/reminder/run-now");
      toast.success(`Ejecutado · ${data.sent || 0}/${data.total || 0} enviados`);
      load();
    } catch (e) {
      toast.error("Error al ejecutar");
    } finally {
      setRunningNow(false);
    }
  };

  if (!cfg) return null;

  const days = [
    { id: "mon", label: "Lun" }, { id: "tue", label: "Mar" }, { id: "wed", label: "Mié" },
    { id: "thu", label: "Jue" }, { id: "fri", label: "Vie" }, { id: "sat", label: "Sáb" }, { id: "sun", label: "Dom" },
  ];

  return (
    <Card data-testid="reminder-config">
      <CardHeader>
        <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
          <Clock className="w-6 h-6 text-purple-500" /> Recordatorio Automático Semanal
        </CardTitle>
        <div className="text-xs text-gray-600">Envía automáticamente recordatorios de pago a quienes tienen cuotas pendientes.</div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Master toggle */}
        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 bg-gray-50">
          <div>
            <div className="font-semibold">{cfg.enabled ? "✅ Activado" : "⚪ Desactivado"}</div>
            <div className="text-xs text-gray-500">
              {cfg.enabled
                ? `Próximo envío: ${days.find((d) => d.id === cfg.day_of_week)?.label || cfg.day_of_week} a las ${String(cfg.hour).padStart(2, "0")}:${String(cfg.minute).padStart(2, "0")} (${cfg.timezone})`
                : "El recordatorio no se enviará automáticamente"}
            </div>
            {cfg.last_run_at && (
              <div className="text-[11px] text-gray-400 mt-1">Última ejecución: {new Date(cfg.last_run_at).toLocaleString()}</div>
            )}
          </div>
          <Button
            onClick={() => save({ enabled: !cfg.enabled })}
            disabled={saving}
            data-testid="reminder-toggle"
            className={cfg.enabled ? "bg-red-500 hover:bg-red-600 text-white" : "bg-green-500 hover:bg-green-600 text-white"}
          >
            {cfg.enabled ? "Desactivar" : "Activar"}
          </Button>
        </div>

        {/* Day & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Día</Label>
            <div className="flex flex-wrap gap-1 mt-2">
              {days.map((d) => (
                <button
                  key={d.id}
                  onClick={() => save({ day_of_week: d.id })}
                  disabled={saving}
                  data-testid={`reminder-day-${d.id}`}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${cfg.day_of_week === d.id ? "bg-orange-500 text-white" : "bg-gray-100 hover:bg-gray-200"}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Hora ({cfg.timezone})</Label>
            <div className="flex gap-2 mt-2">
              <select
                value={cfg.hour}
                onChange={(e) => save({ hour: parseInt(e.target.value) })}
                disabled={saving}
                data-testid="reminder-hour"
                className="h-10 px-2 rounded border border-gray-300 font-semibold"
              >
                {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                ))}
              </select>
              <span className="self-center font-bold">:</span>
              <select
                value={cfg.minute}
                onChange={(e) => save({ minute: parseInt(e.target.value) })}
                disabled={saving}
                data-testid="reminder-minute"
                className="h-10 px-2 rounded border border-gray-300 font-semibold"
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Channel */}
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Canal</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={() => save({ channel: "sms" })}
              data-testid="reminder-channel-sms"
              className={`p-3 rounded-lg border-2 text-left ${cfg.channel === "sms" ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}
            >
              <div className="font-semibold text-sm">📲 SMS automático</div>
              <div className="text-xs text-gray-600">Twilio · ~$0.008/sms</div>
            </button>
            <button
              onClick={() => save({ channel: "whatsapp" })}
              data-testid="reminder-channel-whatsapp"
              className={`p-3 rounded-lg border-2 text-left ${cfg.channel === "whatsapp" ? "border-green-500 bg-green-50" : "border-gray-200"}`}
            >
              <div className="font-semibold text-sm">💬 WhatsApp links</div>
              <div className="text-xs text-gray-600">Solo prepara URLs (no envía solo)</div>
            </button>
          </div>
        </div>

        {/* Run now button */}
        <div className="border-t pt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">Probar enviando AHORA a todos los pendientes:</div>
          <Button onClick={runNow} disabled={runningNow} variant="outline" data-testid="reminder-run-now">
            {runningNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Ejecutar ahora
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function AutoReplyPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rule being edited (or "new")
  const [form, setForm] = useState({ name: "", keywords: "", response_body: "", enabled: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/autoreply/rules");
      setRules(data.rules);
    } catch (e) {/* */}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: "", keywords: "", response_body: "", enabled: true });
    setEditing("new");
  };
  const openEdit = (rule) => {
    setForm({ name: rule.name, keywords: rule.keywords.join(", "), response_body: rule.response_body, enabled: rule.enabled });
    setEditing(rule.id);
  };

  const save = async () => {
    if (!form.name.trim() || !form.keywords.trim() || !form.response_body.trim()) {
      toast.error("Todos los campos son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        response_body: form.response_body.trim(),
        enabled: form.enabled,
      };
      if (editing === "new") {
        await api.post("/admin/autoreply/rules", payload);
        toast.success("Regla creada");
      } else {
        await api.put(`/admin/autoreply/rules/${editing}`, payload);
        toast.success("Regla actualizada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      await api.put(`/admin/autoreply/rules/${rule.id}`, { ...rule, enabled: !rule.enabled });
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"?`)) return;
    try {
      await api.delete(`/admin/autoreply/rules/${rule.id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <Card data-testid="autoreply-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-cyan-500" /> Auto-Respuestas SMS
          </CardTitle>
          <div className="text-xs text-gray-600 mt-1">Cuando un inscrito envía un SMS con estas palabras clave, el sistema responde automáticamente.</div>
        </div>
        <Button onClick={openNew} data-testid="autoreply-new-btn" className="bg-cyan-500 hover:bg-cyan-600 text-white">
          + Nueva regla
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Aún no hay reglas. Crea la primera.</div>
        ) : (
          <div className="space-y-2">
            {rules.map((rule) => (
              <div key={rule.id} className={`p-4 rounded-xl border-2 transition-colors ${rule.enabled ? "border-cyan-200 bg-cyan-50/30" : "border-gray-200 bg-gray-50 opacity-60"}`} data-testid={`autoreply-rule-${rule.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-bold">{rule.name}</div>
                      {!rule.enabled && <span className="text-[10px] uppercase tracking-widest bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-bold">Pausada</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {rule.keywords.map((kw, i) => (
                        <span key={i} className="text-[10px] uppercase tracking-wider bg-cyan-500 text-white px-2 py-0.5 rounded font-bold">{kw}</span>
                      ))}
                    </div>
                    <div className="text-xs text-gray-700 bg-white p-2 rounded border whitespace-pre-wrap">{rule.response_body}</div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => toggleEnabled(rule)} data-testid={`autoreply-toggle-${rule.id}`}>
                      {rule.enabled ? "Pausar" : "Activar"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(rule)} data-testid={`autoreply-edit-${rule.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(rule)} data-testid={`autoreply-delete-${rule.id}`} className="text-red-600 hover:bg-red-50">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit/New modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6"
            onClick={() => !saving && setEditing(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl"
              data-testid="autoreply-modal"
            >
              <h3 className="font-heading uppercase text-xl mb-4">{editing === "new" ? "Nueva regla" : "Editar regla"}</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="ar-name">Nombre</Label>
                  <input
                    id="ar-name"
                    data-testid="autoreply-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300"
                    placeholder="Ej: Pagar / Pago"
                  />
                </div>
                <div>
                  <Label htmlFor="ar-keywords">Palabras clave (separadas por coma)</Label>
                  <input
                    id="ar-keywords"
                    data-testid="autoreply-keywords"
                    value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300"
                    placeholder="PAGAR, PAGO, PAY"
                  />
                  <div className="text-[11px] text-gray-500 mt-1">Si el SMS empieza con o contiene alguna palabra, se dispara esta auto-respuesta.</div>
                </div>
                <div>
                  <Label htmlFor="ar-body">Mensaje de respuesta</Label>
                  <Textarea
                    id="ar-body"
                    data-testid="autoreply-body"
                    value={form.response_body}
                    onChange={(e) => setForm({ ...form, response_body: e.target.value })}
                    rows={4}
                    placeholder="Escribe el mensaje que se enviará automáticamente..."
                    className="mt-1"
                  />
                  <div className="text-[11px] text-gray-500 mt-1">{form.response_body.length}/1500 caracteres</div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    data-testid="autoreply-enabled"
                    className="rounded"
                  />
                  <span className="text-sm">Activar regla inmediatamente</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
                <Button onClick={save} disabled={saving} data-testid="autoreply-save" className="bg-cyan-500 hover:bg-cyan-600 text-white">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

function PromotionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", max_winners: 5, condition: "first_week", public_text: "", enabled: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/promotions");
      setItems(data.promotions);
    } catch (e) {/* */}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const openNew = () => {
    setForm({ name: "", max_winners: 5, condition: "first_week", public_text: "¡Los primeros 5 en pagar su primera cuota reciben un regalo!", enabled: true });
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ name: p.name, max_winners: p.max_winners, condition: p.condition, public_text: p.public_text, enabled: p.enabled });
    setEditing(p.id);
  };

  const save = async () => {
    if (!form.name.trim() || !form.public_text.trim()) { toast.error("Llena todos los campos"); return; }
    setSaving(true);
    try {
      const payload = { ...form, max_winners: parseInt(form.max_winners) };
      if (editing === "new") {
        await api.post("/admin/promotions", payload);
        toast.success("Promoción creada");
      } else {
        await api.put(`/admin/promotions/${editing}`, payload);
        toast.success("Promoción actualizada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (p) => {
    try { await api.put(`/admin/promotions/${p.id}`, { ...p, enabled: !p.enabled }); load(); }
    catch (e) { toast.error("Error"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar la promoción "${p.name}"?`)) return;
    try { await api.delete(`/admin/promotions/${p.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error("Error"); }
  };

  const condLabel = { registered: "Solo inscribirse", first_week: "Pagar 1ra cuota", second_week: "Pagar 2 cuotas", third_week: "Pagar 3 cuotas", fully_paid: "Pagar todo" };

  return (
    <Card data-testid="promotions-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
            <Gift className="w-6 h-6 text-purple-500" /> Promociones Especiales
          </CardTitle>
          <div className="text-xs text-gray-600 mt-1">Cuando un inscrito cumpla la condición, automáticamente se le agregará como ganador en la página pública (con confeti y todo 🎉).</div>
        </div>
        <Button onClick={openNew} data-testid="promo-new-btn" className="bg-purple-500 hover:bg-purple-600 text-white">
          + Nueva promoción
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
          : items.length === 0 ? <div className="text-center py-8 text-gray-500">No hay promociones todavía.</div>
          : (
            <div className="space-y-3">
              {items.map((p) => (
                <div key={p.id} className={`p-4 rounded-xl border-2 ${p.enabled ? "border-purple-200 bg-purple-50/30" : "border-gray-200 bg-gray-50 opacity-60"}`} data-testid={`promo-row-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <div className="font-bold text-lg">{p.name}</div>
                        {!p.enabled && <span className="text-[10px] uppercase tracking-widest bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-bold">Pausada</span>}
                        <span className="text-[10px] uppercase tracking-widest bg-purple-500 text-white px-2 py-0.5 rounded-full font-bold">{condLabel[p.condition]}</span>
                      </div>
                      <div className="text-sm text-gray-700 mb-2">{p.public_text}</div>
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy className="w-4 h-4 text-purple-500" />
                        <span className="font-bold">{(p.winners || []).length}/{p.max_winners}</span>
                        <span className="text-xs text-gray-500">ganadores</span>
                      </div>
                      {(p.winners || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {p.winners.map((w) => (
                            <div key={w.position} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/15 border border-purple-300 rounded-full text-xs font-bold">
                              <Trophy className="w-3 h-3" />#{w.position} {w.full_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="outline" onClick={() => toggleEnabled(p)} data-testid={`promo-toggle-${p.id}`}>{p.enabled ? "Pausar" : "Activar"}</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(p)} className="text-red-600 hover:bg-red-50"><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </CardContent>

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-6" onClick={() => !saving && setEditing(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl" data-testid="promo-modal">
              <h3 className="font-heading uppercase text-xl mb-4">{editing === "new" ? "Nueva promoción" : "Editar promoción"}</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="promo-name">Nombre</Label>
                  <input id="promo-name" data-testid="promo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300" placeholder="Ej: Premio Madrugadores" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="promo-max">Cantidad de ganadores</Label>
                    <input id="promo-max" data-testid="promo-max" type="number" min="1" max="100" value={form.max_winners} onChange={(e) => setForm({ ...form, max_winners: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300" />
                  </div>
                  <div>
                    <Label htmlFor="promo-cond">Condición</Label>
                    <select id="promo-cond" data-testid="promo-cond" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300">
                      <option value="registered">Solo inscribirse</option>
                      <option value="first_week">Pagar 1ra cuota</option>
                      <option value="second_week">Pagar 2 cuotas</option>
                      <option value="third_week">Pagar 3 cuotas</option>
                      <option value="fully_paid">Pagar todo</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="promo-text">Texto público (lo que verá la gente)</Label>
                  <Textarea id="promo-text" data-testid="promo-text" value={form.public_text} onChange={(e) => setForm({ ...form, public_text: e.target.value })}
                    rows={3} placeholder="¡Los primeros 5 en pagar su primera cuota reciben un regalo!" className="mt-1" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="promo-enabled" className="rounded" />
                  <span className="text-sm">Activar inmediatamente</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
                <Button onClick={save} disabled={saving} data-testid="promo-save" className="bg-purple-500 hover:bg-purple-600 text-white">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}


function SquareRecurringControl() {
  const [completed, setCompleted] = useState([]);
  const [overpayments, setOverpayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        api.get("/admin/payments/complete-list"),
        api.get("/admin/overpayments"),
      ]);
      setCompleted(c.data.campers || []);
      setOverpayments(o.data.overpayments || []);
    } catch (e) { /* */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markRefunded = async (id) => {
    try {
      await api.post(`/admin/overpayments/${id}/mark-refunded`);
      toast.success("Marcado como reembolsado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-US", { day:"2-digit", month:"short", year:"numeric" }); }
    catch { return iso; }
  };

  const pendingOver = overpayments.filter(o => !o.refunded);

  return (
    <Card className="border-red-200">
      <CardHeader>
        <CardTitle className="text-red-700 flex items-center gap-2">
          🚨 Control de cobros automáticos de Square
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Square cobra automáticamente cada semana a quienes guardaron su tarjeta. Cuando un campista termine de pagar, <strong>cancela su Card on File en Square Dashboard</strong> para que no se le cobre de más.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overpayments */}
        {pendingOver.length > 0 && (
          <div className="border-2 border-red-400 rounded-xl bg-red-50 p-4">
            <h3 className="font-black text-red-900 mb-3 flex items-center gap-2">
              ⚠️ Sobrepagos detectados ({pendingOver.length})
            </h3>
            <div className="space-y-2">
              {pendingOver.map(o => (
                <div key={o.id} className="bg-white border border-red-300 rounded-lg p-3" data-testid={`overpayment-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-red-900 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300">
                          {o.camper_number_display || "?"}
                        </span>
                        {o.full_name}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        Cobrado: <strong>${o.amount}</strong> · Sobrante: <strong className="text-red-700">${o.overpaid_amount || o.amount}</strong>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        📞 {o.phone} · {fmtDate(o.created_at)}
                        {o.was_already_complete && <span className="ml-2 px-1.5 py-0.5 bg-red-200 text-red-900 rounded font-bold text-[10px]">YA HABÍA TERMINADO</span>}
                      </div>
                      <div className="text-[11px] text-orange-700 font-mono mt-1 truncate">
                        Square ID: {o.square_payment_id}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => markRefunded(o.id)}
                      data-testid={`mark-refunded-${o.id}`}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      ✓ Reembolsado
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed campers list */}
        <div className="border-2 border-amber-300 rounded-xl bg-amber-50 p-4">
          <h3 className="font-black text-amber-900 mb-3 flex items-center gap-2">
            ✅ Ya pagaron completo ({completed.length})
          </h3>
          <p className="text-xs text-amber-800 mb-3">
            <strong>Acción requerida:</strong> entra a Square → busca a estos campistas → cancela su Card on File / Subscription para evitar cobros automáticos extra.
          </p>
          {loading && <div className="text-sm text-gray-500">Cargando...</div>}
          {!loading && completed.length === 0 && (
            <div className="text-sm text-gray-500 italic">Aún nadie ha completado todo su pago.</div>
          )}
          {completed.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {completed.map(c => (
                <div key={c.id} className="bg-white border border-amber-200 rounded-lg p-2.5 flex items-center gap-3" data-testid={`completed-${c.id}`}>
                  <div className="text-xs font-mono px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 border border-yellow-300 flex-shrink-0">
                    {c.camper_number_display || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{c.full_name}</div>
                    <div className="text-[11px] text-gray-500">📞 {c.phone} · pagó ${c.paid_amount} · último pago {fmtDate(c.last_payment_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
          💡 <strong>Tip permanente:</strong> en Square Dashboard → Online → Payment Links → tu link &ldquo;Operación Rescate&rdquo; → desactiva &ldquo;Save card for future payments&rdquo; o &ldquo;Recurring&rdquo; para que ya no se cobre automáticamente a nadie.
        </div>
      </CardContent>
    </Card>
  );
}


function EditPaymentsModal({ reg, onClose, onUpdated }) {
  const [saving, setSaving] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [pendingPayment, setPendingPayment] = useState(null); // { installment, method, note }

  const loadTxs = async () => {
    try {
      const { data } = await api.get(`/admin/registrations/${reg.id}/transactions`);
      setTxs(data.transactions || []);
    } catch { /* */ } finally { setLoadingTx(false); }
  };
  useEffect(() => { loadTxs(); }, []); // eslint-disable-line

  const startMarkPaid = (installment) => {
    setPendingPayment({ installment, method: "zelle", note: "" });
  };

  const confirmMarkPaid = async () => {
    if (!pendingPayment) return;
    const { installment, method, note } = pendingPayment;
    setSaving(installment.installment_number);
    try {
      const { data } = await api.post(`/admin/registrations/${reg.id}/installments/toggle`, {
        installment_number: installment.installment_number,
        status: "paid",
        method,
        note: note || `Marcado manualmente como pagado vía ${method}`,
      });
      onUpdated(data);
      loadTxs();
      toast.success(`Cuota #${installment.installment_number} marcada como pagada (${method})`);
      setPendingPayment(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(null);
    }
  };

  const unmarkPaid = async (installment) => {
    if (!window.confirm(`¿Quitar el pago de la cuota #${installment.installment_number} ($${installment.amount})?`)) return;
    setSaving(installment.installment_number);
    try {
      const { data } = await api.post(`/admin/registrations/${reg.id}/installments/toggle`, {
        installment_number: installment.installment_number,
        status: "pending",
        note: "removido manualmente desde admin",
      });
      onUpdated(data);
      loadTxs();
      toast.success("Pago removido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(null);
    }
  };

  const deleteTx = async (tx) => {
    if (!window.confirm(`¿Borrar transacción de $${tx.amount}?\nSi era la única que cubría la cuota, esta volverá a quedar pendiente.`)) return;
    try {
      const { data } = await api.delete(`/admin/registrations/${reg.id}/transactions/${tx.id}`);
      onUpdated(data);
      loadTxs();
      toast.success("Transacción eliminada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("es-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
      onClick={() => !saving && onClose()}
      data-testid="edit-payments-modal"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl my-auto"
      >
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-gray-500">Editar pagos</div>
              <h3 className="font-heading text-xl truncate">{reg.full_name}</h3>
              <div className="text-xs text-gray-500 mt-0.5">
                {reg.camper_number_display} · {reg.plan_type} · pagó ${reg.paid_amount} de ${reg.total_amount}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-800 p-1" data-testid="edit-payments-close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {/* Installments grid */}
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Cuotas del plan</div>
          <div className="space-y-2 mb-6">
            {(reg.schedule || []).map((s) => {
              const isPaid = s.status === "paid";
              return (
                <div
                  key={s.installment_number}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${isPaid ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}
                  data-testid={`installment-row-${s.installment_number}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${isPaid ? "bg-green-500 text-white" : "bg-gray-300 text-gray-700"}`}>
                    {s.installment_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-base">${s.amount}</div>
                    <div className="text-xs text-gray-500">
                      {isPaid ? `Pagada · ${fmtDate(s.paid_at)}` : `Vence ${s.due_date}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => s.status === "paid" ? unmarkPaid(s) : startMarkPaid(s)}
                    disabled={saving === s.installment_number}
                    data-testid={`toggle-installment-${s.installment_number}`}
                    className={isPaid
                      ? "bg-red-500 hover:bg-red-600 text-white"
                      : "bg-green-500 hover:bg-green-600 text-white"}
                  >
                    {saving === s.installment_number
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : isPaid ? "Quitar pago" : "Marcar pagada"}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Transactions history */}
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Historial de transacciones</div>
          {loadingTx ? (
            <div className="text-sm text-gray-500">Cargando...</div>
          ) : txs.length === 0 ? (
            <div className="text-sm text-gray-500 italic">Sin transacciones registradas.</div>
          ) : (
            <div className="space-y-1.5">
              {txs.map((tx) => {
                const isReversed = tx.payment_status === "reversed" || tx.status === "reversed";
                const methodInfo = (() => {
                  const m = tx.method || tx.metadata?.method || tx.metadata?.kind || "stripe";
                  if (m.includes("zelle")) return { emoji: "💸", label: "Zelle", color: "bg-purple-100 text-purple-700" };
                  if (m.includes("cash_app")) return { emoji: "💚", label: "Cash App", color: "bg-green-100 text-green-700" };
                  if (m.includes("square") || m === "square") return { emoji: "🟦", label: "Square", color: "bg-blue-100 text-blue-700" };
                  if (m === "cash") return { emoji: "💵", label: "Efectivo", color: "bg-yellow-100 text-yellow-800" };
                  if (m.includes("transfer")) return { emoji: "🏦", label: "Transfer", color: "bg-indigo-100 text-indigo-700" };
                  if (m.includes("manual_unmark")) return { emoji: "↩️", label: "Reversado", color: "bg-gray-200 text-gray-600" };
                  if (m.includes("manual")) return { emoji: "📝", label: "Manual", color: "bg-orange-100 text-orange-700" };
                  if (m === "stripe") return { emoji: "💳", label: "Stripe", color: "bg-purple-100 text-purple-700" };
                  return { emoji: "📝", label: m, color: "bg-gray-100 text-gray-700" };
                })();
                return (
                  <div
                    key={tx.id || tx.session_id}
                    className={`flex items-center gap-2 p-2 rounded border text-xs ${isReversed ? "bg-gray-100 border-gray-200 text-gray-500" : "bg-white border-gray-200"}`}
                    data-testid={`tx-row-${tx.id}`}
                  >
                    <div className={`text-[10px] uppercase font-bold rounded px-1.5 py-0.5 flex items-center gap-1 ${methodInfo.color}`}>
                      <span>{methodInfo.emoji}</span> {methodInfo.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">
                        ${tx.amount}
                        {tx.installment_number && <span className="text-gray-500 font-normal"> · cuota #{tx.installment_number}</span>}
                        {isReversed && <span className="ml-2 text-red-600 font-bold">REVERSADO</span>}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {fmtDate(tx.created_at)}
                        {tx.metadata?.square_payment_id && ` · sq:${tx.metadata.square_payment_id.slice(0,12)}`}
                        {tx.metadata?.note && ` · ${tx.metadata.note}`}
                      </div>
                    </div>
                    {!isReversed && (
                      <button
                        onClick={() => deleteTx(tx)}
                        data-testid={`delete-tx-${tx.id}`}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1"
                        title="Borrar transacción"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} className="w-full">Cerrar</Button>
        </div>
      </motion.div>

      {/* Method picker sub-modal */}
      {pendingPayment && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => !saving && setPendingPayment(null)}
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center px-4"
          data-testid="method-picker-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">Marcar como pagado</div>
                <h3 className="font-heading text-lg">Cuota #{pendingPayment.installment.installment_number} · ${pendingPayment.installment.amount}</h3>
              </div>
              <button onClick={() => setPendingPayment(null)} className="text-gray-400 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2">¿Cómo pagó?</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { id: "zelle", label: "Zelle", emoji: "💸" },
                { id: "cash_app", label: "Cash App", emoji: "💚" },
                { id: "square", label: "Square", emoji: "🟦" },
                { id: "cash", label: "Efectivo", emoji: "💵" },
                { id: "transfer", label: "Transferencia", emoji: "🏦" },
                { id: "other", label: "Otro", emoji: "📝" },
              ].map((m) => {
                const active = pendingPayment.method === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setPendingPayment({ ...pendingPayment, method: m.id })}
                    data-testid={`method-${m.id}`}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${active ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <div className="text-2xl mb-1">{m.emoji}</div>
                    <div className="font-bold text-sm">{m.label}</div>
                  </button>
                );
              })}
            </div>

            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Nota / referencia (opcional)</label>
            <input
              type="text"
              value={pendingPayment.note}
              onChange={(e) => setPendingPayment({ ...pendingPayment, note: e.target.value })}
              placeholder="Ej: Zelle de la mamá #ABC123"
              data-testid="payment-note-input"
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
            />

            <div className="flex gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => setPendingPayment(null)}
                disabled={!!saving}
                className="flex-1"
                data-testid="cancel-mark-paid"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmMarkPaid}
                disabled={!!saving}
                data-testid="confirm-mark-paid"
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "✓ Confirmar pago"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

